import { createContext, ReactNode, useEffect, useReducer } from 'react';
// utils
// import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
// @types
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';
import { post, get } from 'src/api/api';
import jwtDecode from 'jwt-decode';
import { decode } from 'punycode';

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
    company: null | Record<string, any>;
  };
  [Types.Login]: {
    user: AuthUser;
    company: null | Record<string, any>;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  company: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        company: action.payload.company,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        company: action.payload.company,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          const decoded = jwtDecode<{ exp: number; permissions: string[] }>(accessToken);

          const response = await get('auth/me');
          // const { user } = response.data;
          const resCompany = await get(`company?id=${response.data.companyId}`);
          localStorage.setItem('cmp', JSON.stringify(resCompany.data[0]));

          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user: {
                ...response.data,
                permissions: decoded && decoded.permissions ? decoded.permissions : [],
              },
              company: resCompany.data[0],
            },
          });
        } else {
          localStorage.removeItem('ftrender');
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
              company: null,
            },
          });
          setSession(null);
        }
      } catch (err) {
        console.error(err);
        localStorage.removeItem('ftrender');
        localStorage.removeItem('cmp');
        setSession(null);

        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
            company: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await post('auth/login', {
      email,
      password,
    });
    const { token, user } = response.data;

    const decoded = jwtDecode<{ exp: number; permissions: string[] }>(token);

    user.permissions = decoded && decoded.permissions ? decoded.permissions : [];

    let resCompany: any = [];

    if (user.companyId) {
      resCompany = await get(`company?id=${user.companyId}`);
      localStorage.setItem('cmp', JSON.stringify(resCompany.data[0]));
    }

    setSession(token);
    localStorage.setItem('fa', 'false');
    dispatch({
      type: Types.Login,
      payload: {
        user,
        company: user.companyId ? resCompany.data[0] : null,
      },
    });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    const response = await post('api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);
    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: Types.Logout });
    localStorage.removeItem('cmp');
    localStorage.removeItem('ftrender');
    localStorage.setItem('fa', 'false');
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };

// export const useJWTContext = () => useContext(AuthContext);
